/**
 * Pure helpers for persisting a chat session to localStorage so a page
 * refresh or reopened tab can rejoin instead of starting over.
 *
 * Kept free of React imports so it can be unit tested in isolation and
 * reused by {@link ChatSocketProvider}.
 */
import { Message, MessageSchema } from '@monetize/types/app';
import { z } from 'zod';
import { logger } from './logger';

const StoredMessageSchema = MessageSchema.extend({
  timestamp: z.coerce.date(),
});

const StoredChatSessionSchema = z.object({
  sessionId: z.string(),
  messages: z.array(StoredMessageSchema).min(1),
});

export interface StoredChatSession {
  sessionId: string;
  messages: Message[];
}

export function getChatSessionStorageKey(
  tenantId: string,
  userId: string,
  agent: string,
): string {
  return `mn:chat:${tenantId}:${userId}:${agent}:session`;
}

export function loadStoredChatSession(
  tenantId: string,
  userId: string,
  agent: string,
): StoredChatSession | null {
  try {
    const raw = localStorage.getItem(
      getChatSessionStorageKey(tenantId, userId, agent),
    );
    if (!raw) {
      return null;
    }
    const parsed = StoredChatSessionSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch (err) {
    logger.warn('chatSessionStorage: failed to load stored session', err);
    return null;
  }
}

export function saveStoredChatSession(
  tenantId: string,
  userId: string,
  agent: string,
  session: StoredChatSession,
): void {
  try {
    localStorage.setItem(
      getChatSessionStorageKey(tenantId, userId, agent),
      JSON.stringify(session),
    );
  } catch (err) {
    logger.warn('chatSessionStorage: failed to persist session', err);
  }
}
